import { useTenant } from '@/hooks';
import {
  useAddProjectToBoardMutation,
  useGetAllBoardsQuery,
  useGetCurrentUserQuery,
} from '@/state';
import {
  useCallback,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Button } from '../ui/button';
import type { ApiErrorResponse, ProjectResponse } from '@/types';
import { toast } from 'sonner';
import { getMessageByStatus } from '@/lib';
import { Save } from 'lucide-react';

interface ShowBoardsProps {
  show: ProjectResponse | null;
  onClose: Dispatch<SetStateAction<ProjectResponse | null>>;
}
const ShowBoards: React.FC<ShowBoardsProps> = ({ show, onClose: setOpen }) => {
  if (!show) {
    return null;
  }
  const [selectedBoard, setSelectedBoad] = useState<{
    board: number; /** board === boardId */
  } | null>(null);
  const { tenant } = useTenant();

  const [addProjectToBoard, { isLoading: isAdding }] =
    useAddProjectToBoardMutation();
  const { data: currentUser } = useGetCurrentUserQuery(
    {
      tenant: tenant!,
    },
    {
      skip: !tenant,
    }
  );

  const { data: allAvailableBoards, isLoading } = useGetAllBoardsQuery(
    {
      tenant: tenant!,
      userId: currentUser?.id,
    },
    {
      skip: !tenant || !show || !currentUser,
    }
  );


  const addProjectOnBoard = useCallback(async () => {
    try {
      await addProjectToBoard({
        tenant: tenant!,
        projectId: show.id,
        boardId: selectedBoard!.board,
      }).unwrap();

      setOpen(null);
      toast.success(`Project added to board successfully.`);
    } catch (err) {
      const error = err as ApiErrorResponse;
      toast.error(`Error`, {
        description: getMessageByStatus(error?.status || 500),
      });
    }
  }, [selectedBoard]);

  return (
    <>
      <Dialog open={!!show} onOpenChange={() => setOpen(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Boards</DialogTitle>
            <DialogDescription>Select a board</DialogDescription>
          </DialogHeader>
          <Select
            onValueChange={(e) =>
              setSelectedBoad({
                board: Number(e),
              })
            }
          >
            <SelectTrigger className="w-full">
              <SelectValue
                placeholder={isLoading ? 'Loading...' : 'Select a board'}
              />
            </SelectTrigger>
            <SelectContent>
              {allAvailableBoards?.map((board) => (
                <SelectItem key={board.id} value={String(board.id)}>
                  {board.board_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex justify-end">
            {selectedBoard && (
              <Button
                className="mt-4"
                onClick={addProjectOnBoard}
                disabled={isAdding}
              >
                <Save/>{isAdding ? 'Saving...' : 'Save'}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ShowBoards;
